import { useEffect, useState, useRef, useMemo, useCallback, ChangeEvent, MouseEvent, KeyboardEvent } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { Input } from '@/UI/Input/Input';
import { SearchIcon } from '@/UI/Icons/SearchIcon';
import { I18N_KEYS } from '@/language/keys';
import { useNodeFiltersStore } from '@/state/nodes/nodes.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { MenuCategory, ModelItem } from '@/state/nodes/nodes.types';
import {
  flattenMenuData,
  filterNodesBySearch,
  filterNodesByInputOutputTypes,
  getWasmItemIds,
} from '@/state/nodes/nodes.utils';
import { HandleType } from '@/enums/handle-type.enum';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { FF_SUBFLOWS_SAVED_NODES } from '@/consts/featureFlags';
import { useListSubflowTemplates } from '@/queries/useSubflowTemplates';
import { MENU_ITEM_ID_TEXT_MAP } from '../FlowComponents/LeftPanelSections/MenuItemIdTextsMap';
import { getOppositeHandleKeyForConnection } from '../utils';
import ParentMenuItem from './ParentMenuItem';
import type { NodeType, ModelNodeType } from 'web';
import type { AddNewNodeOptions } from '../flowTypes';
import type { Connection } from '../FlowComponents/FlowTour/ConnectionContext';
import type { AiModelData } from '@/types/nodes/model';

interface AddNodeMenuProps {
  positionX: number;
  positionY: number;
  connection?: Connection;
  addNewNode: ({ action, dropX, dropY, connection }: AddNewNodeOptions) => void;
  onClose: () => void;
  autoFocus?: boolean;
}

/**
 * Reusable menu content for adding nodes to the canvas
 * Can be used standalone or inside other menus like ParentMenuItem
 */
export function AddNodeMenu({
  positionX,
  positionY,
  addNewNode,
  onClose,
  connection,
  autoFocus = true,
}: AddNodeMenuProps) {
  const [search, setSearch] = useState('');
  const [filteredMenuItems, setFilteredMenuItems] = useState<ModelItem[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const { menu } = useNodeFiltersStore();
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);
  const isSavedNodesEnabled = useFeatureFlagEnabled(FF_SUBFLOWS_SAVED_NODES);
  const { data: templates, isLoading: isTemplatesLoading } = useListSubflowTemplates(undefined, {
    enabled: !!isSavedNodesEnabled,
  });

  const savedTemplateIds = new Set(
    [...(menu.savedModels?.children ?? []), ...(menu.savedNodes?.children ?? [])].map((c) => c.id),
  );

  const resolveAction = useCallback(
    (item: ModelItem): AddNewNodeOptions['action'] => {
      const template = templates?.find((t) => t.id === item.id);
      if (!template) {
        return item;
      }
      return {
        // TODO: verify — uses the original node definition ID (e.g. "wildcard") to instantiate
        // the node type, not the template's own ID. Pre-existing behavior, may need revisiting.
        id: template.sourceNodeDefinitionId ?? item.id,
        type: template.type,
        name: item.name,
        displayName: item.displayName,
        savedTemplateData: template.data,
      };
    },
    [templates],
  );

  // Build lookups: menu item ID → WASM kind. Tool nodes and models are separated
  const { nodeKindById, modelKindById } = useMemo(() => {
    const nodeMap = new Map<ModelItem['id'], NodeType>();
    const modelMap = new Map<ModelItem['id'], ModelNodeType>();
    nodeTypes.forEach((nt) => {
      if (nt.type) {
        if (nt.isModel) {
          const modelType = (nt.data as AiModelData)?.kind?.type;
          if (modelType) modelMap.set(nt.id, modelType as ModelNodeType);
        } else {
          nodeMap.set(nt.id, nt.type as NodeType);
        }
      }
    });
    return { nodeKindById: nodeMap, modelKindById: modelMap };
  }, [nodeTypes]);

  const originalFilteredItems = useRef<ModelItem[]>([]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value);

  // On Arrow Up/Down on the first or last item, focus the search input
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const currentItem = e.currentTarget as HTMLLIElement;
    const menuElement = currentItem.closest('[role="menu"]');

    if (!menuElement) return;

    const menuItems = Array.from(menuElement.querySelectorAll('li'));
    const currentIndex = menuItems.indexOf(currentItem);

    if (currentIndex === -1) return;

    const isFirstItem = currentIndex === 0;
    const isLastItem = currentIndex === menuItems.length - 1;

    // Only handle arrow keys at boundaries
    if (e.key === 'ArrowUp' && isFirstItem) {
      e.preventDefault();
      e.stopPropagation();
      searchInputRef.current?.focus();
    } else if (e.key === 'ArrowDown' && isLastItem) {
      e.preventDefault();
      e.stopPropagation();
      searchInputRef.current?.focus();
    }
  }, []);

  const handleSearchKeyDown = (e: KeyboardEvent) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && filteredMenuItems.length !== 0) {
      return;
    }
    if (e.key === 'Enter') {
      if (filteredMenuItems.length !== 1) {
        return;
      }

      addNewNode({ action: resolveAction(filteredMenuItems[0]), dropX: positionX, dropY: positionY, connection });
      onClose();
    }

    if (e.key === 'Escape') {
      onClose();
    }
    e.stopPropagation();
  };

  const handleItemClick = (e: MouseEvent, item: ModelItem) => {
    addNewNode({ action: resolveAction(item), dropX: positionX, dropY: positionY, connection });
    onClose();
  };

  const wasmItemIds = useMemo(() => {
    if (!connection?.handleType || connection.handleType === HandleType.Any) return undefined;
    return getWasmItemIds([connection.handleType], nodeKindById, modelKindById);
  }, [connection?.handleType, nodeKindById, modelKindById]);

  // Items in wasmKnownIds use WASM as source of truth (priority over backend).
  // Wildcard models return [] from WASM's static API, so they're correctly excluded.
  const wasmKnownIds = useMemo(
    () => new Set<ModelItem['id']>([...nodeKindById.keys(), ...modelKindById.keys()]),
    [nodeKindById, modelKindById],
  );

  useEffect(() => {
    if (connection) {
      const typeKey = getOppositeHandleKeyForConnection(connection.handleSide);
      const types = connection.handleType ? [connection.handleType] : [];
      const filteredItems = filterNodesByInputOutputTypes(
        flattenMenuData(menu),
        typeKey,
        types,
        wasmItemIds,
        wasmKnownIds,
      );
      setFilteredMenuItems(filteredItems);
      originalFilteredItems.current = [...filteredItems];
    }
  }, [connection, menu, wasmItemIds, wasmKnownIds]);

  useEffect(() => {
    if (connection && originalFilteredItems.current.length > 0) {
      const filtered = filterNodesBySearch(originalFilteredItems.current, search);
      setFilteredMenuItems(filtered);
    } else {
      setFilteredMenuItems(filterNodesBySearch(flattenMenuData(menu), search));
    }
  }, [search, menu, connection]);

  // Helper type guards
  function isMenuCategory(item: MenuCategory | ModelItem): item is MenuCategory {
    return 'children' in item && Array.isArray(item.children);
  }

  function isModelItem(item: MenuCategory | ModelItem): item is ModelItem {
    return !('children' in item) || !Array.isArray(item.children);
  }

  const renderMenu = (items: MenuCategory['children'], shouldFocusSearch = false) => {
    return items.map((item) => {
      if (!item || typeof item.id !== 'string') return null;

      if (isMenuCategory(item)) {
        const isSavedSubcategory = item.id === 'saved_models' || item.id === 'saved_nodes';
        const isEmpty = !item.children || item.children.length === 0;

        // Only render ParentMenuItem if it has at least one child, unless it's a saved subcategory (show as disabled)
        if (isEmpty && !isSavedSubcategory) {
          return null;
        }

        const translationKey =
          typeof item.id === 'string'
            ? (MENU_ITEM_ID_TEXT_MAP[
                item.id as keyof typeof MENU_ITEM_ID_TEXT_MAP
              ] as keyof typeof I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.MENU_TITLES)
            : undefined;
        return (
          <ParentMenuItem
            id={item.id}
            label={translationKey ? t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.MENU_TITLES[translationKey]) : item.id}
            onClose={onClose}
            key={item.id}
            contextMenuWidth="240px"
            onKeyDown={shouldFocusSearch ? handleKeyDown : undefined}
            disabled={isSavedSubcategory && isEmpty}
          >
            {renderMenu(item.children)}
          </ParentMenuItem>
        );
      } else if (isModelItem(item)) {
        const translationKey =
          typeof item.id === 'string'
            ? (MENU_ITEM_ID_TEXT_MAP[
                item.id as keyof typeof MENU_ITEM_ID_TEXT_MAP
              ] as keyof typeof I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.MENU_TITLES)
            : undefined;
        const isSavedItem = savedTemplateIds.has(item.id);
        const element = (
          <AppMenuItem
            key={item.id}
            onClick={(e) => handleItemClick(e, item)}
            onKeyDown={shouldFocusSearch ? handleKeyDown : undefined}
            disabled={isSavedItem && isTemplatesLoading}
          >
            <EllipsisText sx={{ flex: 1 }} variant="body-sm-rg">
              {translationKey ? t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.MENU_TITLES[translationKey]) : item.displayName}
            </EllipsisText>
          </AppMenuItem>
        );
        return element;
      }
      return null;
    });
  };

  // Render recent items flat at the top
  const renderRecentItems = (recentItems: ModelItem[]) => {
    return recentItems.map((item) => {
      const translationKey =
        typeof item.id === 'string'
          ? (MENU_ITEM_ID_TEXT_MAP[
              item.id as keyof typeof MENU_ITEM_ID_TEXT_MAP
            ] as keyof typeof I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.MENU_TITLES)
          : undefined;

      return (
        <AppMenuItem key={`recent-${item.id}`} onClick={(e) => handleItemClick(e, item)} onKeyDown={handleKeyDown}>
          <EllipsisText sx={{ flex: 1 }} variant="body-sm-rg">
            {translationKey ? t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.MENU_TITLES[translationKey]) : item.displayName}
          </EllipsisText>
        </AppMenuItem>
      );
    });
  };

  const renderSearchResults = () => {
    return filteredMenuItems.map((item, index: number) => {
      const translationKey =
        typeof item.id === 'string'
          ? (MENU_ITEM_ID_TEXT_MAP[
              item.id as keyof typeof MENU_ITEM_ID_TEXT_MAP
            ] as keyof typeof I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.MENU_TITLES)
          : undefined;

      const isSavedItem = savedTemplateIds.has(item.id);
      return (
        <AppMenuItem
          key={item.id || index}
          onClick={(e) => handleItemClick(e, item)}
          onKeyDown={handleKeyDown}
          disabled={isSavedItem && isTemplatesLoading}
        >
          <EllipsisText sx={{ flex: 1 }} variant="body-sm-rg">
            {translationKey ? t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.MENU_TITLES[translationKey]) : item.displayName}
          </EllipsisText>
        </AppMenuItem>
      );
    });
  };

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [autoFocus]);

  return (
    <>
      <Box sx={{ p: 0, pb: 0.5 }}>
        <Input
          inputRef={searchInputRef}
          value={search}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          autoComplete="off"
          size="small"
          fullWidth
          sx={{
            '&.MuiInputBase-root': {
              minWidth: 'auto',
            },
          }}
          placeholder={t(I18N_KEYS.GENERAL.SEARCH)}
          startAdornment={<SearchIcon />}
        />
      </Box>
      {search || connection
        ? renderSearchResults()
        : [
            ...renderRecentItems((menu.recent?.children || []) as ModelItem[]),
            ...renderMenu(
              [
                ...(isSavedNodesEnabled
                  ? [{ id: 'saved', children: [menu.savedModels, menu.savedNodes] } as MenuCategory]
                  : []),
                menu.tools,
                menu.imageModels,
                menu.videoModels,
                menu.threedModels,
                menu.customModels,
              ],
              true,
            ),
          ]}
    </>
  );
}
